import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { configureTextBuilder } from 'troika-three-text';

import { AuthLifecycle } from '@/components/auth/auth-lifecycle';
import { AuthModeProvider } from '@/components/auth/auth-mode';
import { AuthProvider } from '@/components/auth/auth-provider';
import { UserProvider } from '@/components/auth/user-provider';
import { StaffSessionProvider } from '@/components/auth/staff-session-provider';
import { ThemeProvider } from '@/components/theme/theme-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ChallengeToastProvider } from '@/components/hunt/challenge-toast';
import { buildMsalConfig, isAuthConfigured } from '@/lib/auth-config';
import { App } from './App';
import './index.css';

// Production CSP does not permit Troika's generated worker module or external
// fallback font metadata. Configure the shared text builder before any React
// Three scene can mount so all text assets remain same-origin.
configureTextBuilder({
  useWorker: false,
  defaultFontURL: '/fonts/Roboto-Regular.ttf',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

function AppTree({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <ChallengeToastProvider>
            <BrowserRouter>
              {children}
            </BrowserRouter>
          </ChallengeToastProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

async function bootstrap() {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element #root not found');
  }

  const authEnabled = isAuthConfigured();

  if (authEnabled) {
    // Singleton PCA: initialize before React mounts so redirect responses are
    // hydrated once and MsalProvider can listen continuously.
    const msalInstance = new PublicClientApplication(buildMsalConfig());
    await msalInstance.initialize();

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(accounts[0] ?? null);
    }

    createRoot(rootEl).render(
      <StrictMode>
        <AppTree>
          <AuthModeProvider enabled>
            <MsalProvider instance={msalInstance}>
              <AuthLifecycle />
              <AuthProvider>
                <UserProvider>
                  <StaffSessionProvider>
                    <App />
                  </StaffSessionProvider>
                </UserProvider>
              </AuthProvider>
            </MsalProvider>
          </AuthModeProvider>
        </AppTree>
      </StrictMode>
    );
    return;
  }

  createRoot(rootEl).render(
    <StrictMode>
      <AppTree>
        <AuthModeProvider enabled={false}>
          <AuthProvider>
            <UserProvider>
              <StaffSessionProvider>
                <App />
              </StaffSessionProvider>
            </UserProvider>
          </AuthProvider>
        </AuthModeProvider>
      </AppTree>
    </StrictMode>
  );
}

void bootstrap();
